import { useEffect, useState } from "react";

import AlertDialog from "@components/dialog/AlertDialog";
import Box from "@mui/material/Box";
import { BusinessInformationStep } from "@components/request-access/customer/BusinessInformationStep";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import { CustomerConfirmationStep } from "@components/request-access/new-customer/CustomerConfirmationStep";
import { CustomerInformationStep } from "@components/request-access/customer/CustomerInformationStep";
import { CustomerRequestedPortalStep } from "@components/request-access/customer/CustomerRequestedPortalStep";
import { GetStaticPropsContext } from "next";
import Head from "next/head";
import MobileStepper from "@mui/material/MobileStepper";
import { NewCustomerFormData } from "@components/request-access/new-customer-model";
import { NewCustomerTellUsMoreStep } from "@components/request-access/new-customer/CustomerTellUsMoreStep";
import { RequestSent } from "@components/request-access/RequestSent";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Wrapper } from "@components/request-access/Wrapper";
import { getProductPartnerPortal } from "@components/request-access/product-partner-portal-url";
import { getRequestAccessEndpoint } from "@components/request-access/request-access-api";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useAlertDialog } from "@hooks/useAlertDialog";
import { useCustomerAccessFormik } from "@components/request-access/useCustomerAccessFormik";
import { useCustomerBusinessFormik } from "@components/request-access/useCustomerBusinessFormik";
import { useCustomerRequestedPortalFormik } from "@components/request-access/useCustomerRequestedPortalFormik";
import { useCustomerSummaryFormik } from "@components/request-access/useCustomerSummaryFormik";
import { useGetStepsList } from "@components/request-access/useGetStepsList";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNewCustomerTellUsMoreFormik } from "@components/request-access/useNewCustomerTellUsMoreFormik";
import { useRouter } from "next/navigation";
import { useStepManager } from "../../lib/stepper/useStepManager";
import { useTranslation } from "next-i18next";

export default function RequestAccessNewCustomerPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { getNewCustomerStepsList } = useGetStepsList();

  const [restrictedDomains, setRestrictedDomains] = useState<string[]>([]);

  const { dialogOpen, handleOpenDialog, handleCloseDialog, handleAgreeDialog } =
    useAlertDialog();

  const requestedPortalFormik = useCustomerRequestedPortalFormik(() => {
    let url = "";
    switch (requestedPortalFormik.values.appRefName) {
      case "GPS":
      case "DCP":
        url = getProductPartnerPortal(
          requestedPortalFormik.values.appRefName,
          requestedPortalFormik.values.country.split("|")[1]
        );
        handleOpenDialog(url); // Open the dialog here
        break;
      default:
        handleComplete();
        break;
    }
  });

  const customerInformationFormik = useCustomerAccessFormik(() => {
    handleComplete();
  });

  const customerTellUsMoreFormik = useNewCustomerTellUsMoreFormik(() => {
    handleComplete();
  });

  const customerBusinessFormik = useCustomerBusinessFormik(() => {
    handleComplete();
  });

  const customerSummaryFormik = useCustomerSummaryFormik(() => {
    handleSubmission();
  });

  const steps: string[] = getNewCustomerStepsList();

  const {
    activeStep,
    completed,
    allStepsCompleted,
    handleComplete,
    handleBack,
    handleSetStep,
  } = useStepManager(steps);

  async function handleSubmission() {
    // TODO consider loading indicators
    const result = await submitRequest();
    if (result) {
      handleComplete();
    } else {
      // TODO error handling
      // what should we present to the user
      // they will still be on step 3
      // so we will need a way to send data down to that component,
      // or have it bump down
    }
  }

  async function submitRequest() {
    const frontEndHostName =
      typeof window !== "undefined" ? window.location.host : "ssr";

    const form: NewCustomerFormData = getFormData();

    const payload: NewCustomerFormData & {
      frontEndHostName: string;
      accessType: "newCustomer";
    } = {
      ...form,
      frontEndHostName: frontEndHostName,
      accessType: "newCustomer",
    };

    console.info("🛫 sending customer request access payload", payload);
    const result = await postRequest(payload);

    if (result) {
      console.info("submit request successful");
      return true;
    }

    console.info("submit request failure");
    return false;
  }

  function getFormData() {
    const formData: NewCustomerFormData = {
      ...customerInformationFormik.values,
      ...customerBusinessFormik.values,
      ...requestedPortalFormik.values,
      ...customerTellUsMoreFormik.values,
      ...customerSummaryFormik.values,
    };

    return formData;
  }

  async function postRequest(payload = {}) {
    const postEndpoint = getRequestAccessEndpoint();
    const response = await fetch(postEndpoint, {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      referrerPolicy: "no-referrer",
      body: JSON.stringify(payload),
    });
    const result = response.status >= 200 && response.status <= 204;
    return result;
  }

  const matches = useMediaQuery("(min-width:600px)");

  useEffect(() => {
    customerInformationFormik.setFieldValue(
      "restrictedEmailDomains",
      restrictedDomains
    );
    // TODO: fix this hook dependency inconsistency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedDomains]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  return (
    <>
      <Head>
        <title>{`myCargill - ${t(
          "request_access:select_access_type.current_customer_title"
        )} - ${t("common:header.get_access")}`}</title>
        <meta
          name="description"
          content={t("common:header.get_access") || undefined}
        />
      </Head>
      <AlertDialog
        dialogId="alertdialog"
        open={dialogOpen}
        onClose={handleCloseDialog}
        onAgree={handleAgreeDialog}
        title={t("request_access:dialogs.redirect_title") || ""}
        contextText={t("request_access:dialogs.redirect_content")}
        cancelActionText={t("common:common.cancel")}
        continueActionText={t("common:common.continue")}
      />
      <Wrapper
        top={
          <Container
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: "3rem",
              justifyContent: "center",
            }}
          >
            {matches ? (
              <Stepper activeStep={activeStep} sx={{ width: "100%" }}>
                {steps.map((label, index) => (
                  <Step key={label} completed={completed[index]}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            ) : (
              <MobileStepper
                variant="dots"
                steps={steps.length}
                position="static"
                activeStep={activeStep}
                nextButton={null}
                backButton={null}
              />
            )}
          </Container>
        }
      >
        {allStepsCompleted() ? (
          <>
            <RequestSent />
          </>
        ) : (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ mt: 2, mb: 1, py: 1 }}>
              {/* put conditional content in this box */}
              {/* first step screen */}
              {activeStep === 0 ? (
                <>
                  <CustomerRequestedPortalStep
                    setRestrictedDomains={setRestrictedDomains}
                    formik={requestedPortalFormik}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    color="secondary"
                    onClick={() => router.push("/request-access")}
                  >
                    {t("common:common.cancel")}
                  </Button>
                </>
              ) : null}

              {/* second step screen */}
              {activeStep === 1 ? (
                <>
                  <CustomerInformationStep formik={customerInformationFormik} />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}

              {/* third step screen */}
              {activeStep === 2 ? (
                <>
                  <BusinessInformationStep formik={customerBusinessFormik} />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}

              {/* fourth step screen */}
              {activeStep === 3 ? (
                <>
                  <NewCustomerTellUsMoreStep
                    formik={customerTellUsMoreFormik}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}
              {activeStep == 4 ? (
                <>
                  <CustomerConfirmationStep
                    formData={getFormData()}
                    formik={customerSummaryFormik}
                    handleSetStep={handleSetStep}
                  />
                  <Button
                    sx={{ width: "100%" }}
                    type="reset"
                    color="secondary"
                    onClick={() => handleBack()}
                  >
                    {t("common:common.back")}
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>
        )}
      </Wrapper>
    </>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  return {
    props: {
      ...(await serverSideTranslations(context.locale as string, [
        "common",
        "request_access",
      ])),
    },
  };
}
